import React, { useState, useEffect, useRef } from 'react';
import { FileText, Download, Trash2, ExternalLink, Search, Sparkles, Clock, CheckCircle, AlertCircle, Settings, Plus, Edit2, Save, X, ChevronDown, ChevronUp, Square, CheckSquare, Upload, File } from 'lucide-react';

export default function CaseStudyBuilder() {
  const [clientRequirement, setClientRequirement] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [caseStudies, setCaseStudies] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [testMode, setTestMode] = useState(false);

  // Prompt management state
  const [prompts, setPrompts] = useState([]);
  const [selectedPromptId, setSelectedPromptId] = useState(null);
  const [showPromptManager, setShowPromptManager] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [newPrompt, setNewPrompt] = useState(null);
  const [promptDeleteConfirm, setPromptDeleteConfirm] = useState(null);

  // Settings state
  const [appSettings, setAppSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  // Case study selection state
  const [selectedCaseStudies, setSelectedCaseStudies] = useState([]);
  const [deletingMultiple, setDeletingMultiple] = useState(false);

  const api = process.env.REACT_APP_API_BASE || '';
  const token = localStorage.getItem('token');

  // Load case studies
  const loadCaseStudies = async () => {
    try {
      const res = await fetch(`${api}/api/case-studies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCaseStudies(data);
      }
    } catch (err) {
      console.error('Error loading case studies:', err);
    }
  };

  // Load prompts
  const loadPrompts = async () => {
    try {
      const res = await fetch(`${api}/api/prompts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPrompts(data);
        // Set default prompt as selected
        const defaultPrompt = data.find(p => p.isDefault) || data[0];
        if (defaultPrompt && !selectedPromptId) {
          setSelectedPromptId(defaultPrompt.id);
        }
      }
    } catch (err) {
      console.error('Error loading prompts:', err);
    }
  };

  // Load app settings
  const loadSettings = async () => {
    try {
      const res = await fetch(`${api}/api/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAppSettings(data);
      }
    } catch (err) {
      console.error('Error loading settings:', err);
    }
  };

  // Save app settings
  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await fetch(`${api}/api/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(appSettings)
      });
      if (res.ok) {
        setSuccess({ message: 'Settings saved successfully!' });
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to save settings');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSavingSettings(false);
    }
  };

  useEffect(() => {
    loadCaseStudies();
    loadPrompts();
    loadSettings();
  }, []);

  // Generate case study
  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    setError(null);
    setSuccess(null);

    try {
      const endpoint = testMode ? `${api}/api/case-studies/test` : `${api}/api/case-studies`;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), testMode ? 30000 : 120000);

      const requestBody = { clientRequirement };
      if (!testMode && selectedPromptId) {
        requestBody.promptId = selectedPromptId;
      }
      if (fileContent) {
        requestBody.fileContent = fileContent;
      }

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const data = await res.json();

      if (res.ok) {
        setSuccess({
          message: 'Case study generated successfully!',
          pdfUrl: data.caseStudy.pdfUrl,
          upworkCoverLetter: data.caseStudy.upworkCoverLetter,
          coverLetterSource: data.caseStudy.coverLetterSource,
          promptUsed: data.caseStudy.promptUsed
        });
        setClientRequirement('');
        setUploadedFile(null);
        setFileContent('');
        loadCaseStudies();
      } else {
        setError(data.error || data.message || 'Failed to generate case study');
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        setError('Request timed out. The AI service might be busy. Please try again.');
      } else {
        setError('Network error. Please try again.');
      }
      console.error('Error:', err);
    } finally {
      setGenerating(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a PDF, Word (.doc, .docx), or Excel (.xls, .xlsx) file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setUploadingFile(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${api}/api/parse-file`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        setUploadedFile(file);
        setFileContent(data.content || '');
      } else {
        const errData = await res.json();
        setError(errData.error || 'Failed to parse file');
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      setError('Error uploading file. Please try again.');
    } finally {
      setUploadingFile(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Remove uploaded file
  const removeUploadedFile = () => {
    setUploadedFile(null);
    setFileContent('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Delete case study
  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${api}/api/case-studies/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        loadCaseStudies();
        setDeleteConfirm(null);
        setSelectedCaseStudies(prev => prev.filter(csId => csId !== id));
        setSuccess({ message: 'Case study deleted successfully!' });
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to delete case study');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  // Toggle case study selection
  const toggleCaseStudySelection = (id) => {
    setSelectedCaseStudies(prev =>
      prev.includes(id)
        ? prev.filter(csId => csId !== id)
        : [...prev, id]
    );
  };

  // Select all case studies
  const selectAllCaseStudies = () => {
    if (selectedCaseStudies.length === caseStudies.length) {
      setSelectedCaseStudies([]);
    } else {
      setSelectedCaseStudies(caseStudies.map(cs => cs.id));
    }
  };

  // Delete selected case studies
  const deleteSelectedCaseStudies = async () => {
    if (selectedCaseStudies.length === 0) return;

    setDeletingMultiple(true);
    setError(null);

    try {
      let deletedCount = 0;
      for (const id of selectedCaseStudies) {
        const res = await fetch(`${api}/api/case-studies/${id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) deletedCount++;
      }

      setSelectedCaseStudies([]);
      loadCaseStudies();
      setSuccess({ message: `${deletedCount} case stud${deletedCount === 1 ? 'y' : 'ies'} deleted successfully!` });
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error deleting some case studies. Please try again.');
    } finally {
      setDeletingMultiple(false);
    }
  };

  // Create new prompt
  const handleCreatePrompt = async () => {
    try {
      const res = await fetch(`${api}/api/prompts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newPrompt)
      });

      if (res.ok) {
        loadPrompts();
        setNewPrompt(null);
        setSuccess({ message: 'Prompt created successfully!' });
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to create prompt');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  // Update prompt
  const handleUpdatePrompt = async () => {
    try {
      const res = await fetch(`${api}/api/prompts/${editingPrompt.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(editingPrompt)
      });

      if (res.ok) {
        loadPrompts();
        setEditingPrompt(null);
        setSuccess({ message: 'Prompt updated successfully!' });
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to update prompt');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  // Delete prompt
  const handleDeletePrompt = async (id) => {
    try {
      const res = await fetch(`${api}/api/prompts/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        loadPrompts();
        setPromptDeleteConfirm(null);
        if (selectedPromptId === id) {
          setSelectedPromptId(null);
        }
        setSuccess({ message: 'Prompt deleted successfully!' });
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to delete prompt');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  // Filter case studies
  const filteredCaseStudies = caseStudies.filter(cs =>
    cs.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cs.painPoint.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cs.clientRequirement.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Character count and validation
  const charCount = clientRequirement.length;
  const isValid = charCount >= 50 && charCount <= 5000;
  const charCountColor = charCount < 50 ? 'text-red-500' : charCount > 5000 ? 'text-red-500' : 'text-green-600';

  // Get selected prompt name
  const selectedPrompt = prompts.find(p => p.id === selectedPromptId);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-8 text-white">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <Sparkles className="w-10 h-10" />
            <h1 className="text-4xl font-bold">Case Study Builder</h1>
          </div>
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all"
          >
            <Settings className="w-5 h-5" />
            Settings
          </button>
        </div>
        <p className="text-purple-100 text-lg">
          Generate compelling case studies powered by Claude AI. Cover letters generated with ChatGPT using your custom prompts.
        </p>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Settings className="w-6 h-6 text-purple-600" />
              Case Study Settings
            </h2>
            <button
              onClick={() => setShowSettings(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {!appSettings ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-500">Loading settings...</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Number of Case Studies */}
                <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                  <label className="block text-sm font-semibold text-purple-900 mb-2">
                    Number of Case Studies in PDF
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min={appSettings.caseStudy?.minCaseStudies || 2}
                      max={appSettings.caseStudy?.maxCaseStudies || 5}
                      value={appSettings.caseStudy?.numberOfCaseStudies || 3}
                      onChange={(e) => setAppSettings({
                        ...appSettings,
                        caseStudy: {
                          ...appSettings.caseStudy,
                          numberOfCaseStudies: parseInt(e.target.value)
                        }
                      })}
                      className="flex-1 h-2 bg-purple-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <span className="text-2xl font-bold text-purple-700 w-12 text-center">
                      {appSettings.caseStudy?.numberOfCaseStudies || 3}
                    </span>
                  </div>
                  <p className="text-xs text-purple-600 mt-2">
                    Each case study adds depth to your portfolio PDF. Recommended: 3 case studies.
                  </p>
                </div>

                {/* LinkedIn Settings */}
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <label className="block text-sm font-semibold text-blue-900 mb-2">
                    Default LinkedIn Post Count
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={appSettings.linkedin?.defaultPostCount || 3}
                      onChange={(e) => setAppSettings({
                        ...appSettings,
                        linkedin: {
                          ...appSettings.linkedin,
                          defaultPostCount: parseInt(e.target.value)
                        }
                      })}
                      className="flex-1 h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <span className="text-2xl font-bold text-blue-700 w-12 text-center">
                      {appSettings.linkedin?.defaultPostCount || 3}
                    </span>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    Number of LinkedIn posts to generate by default.
                  </p>
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={saveSettings}
                  disabled={savingSettings}
                  className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-all disabled:opacity-50"
                >
                  {savingSettings ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Settings
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="bg-green-50 border-2 border-green-200 rounded-xl p-5">
          <div className="flex items-start gap-4 mb-4">
            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-green-900 mb-2">{success.message}</h3>
              {success.pdfUrl && (
                <div className="flex items-center gap-4">
                  <a
                    href={success.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View PDF Case Study
                  </a>
                  {success.coverLetterSource && (
                    <span className="text-sm text-green-700 bg-green-100 px-3 py-1 rounded-full">
                      Cover letter by: {success.coverLetterSource === 'openai' ? '🤖 ChatGPT' : '🧠 Claude'}
                      {success.promptUsed && ` (${success.promptUsed})`}
                    </span>
                  )}
                </div>
              )}
            </div>
            <button onClick={() => setSuccess(null)} className="text-green-600 hover:text-green-800">
              ✕
            </button>
          </div>

          {/* Upwork Cover Letter Section */}
          {success.upworkCoverLetter && (
            <div className="mt-6 bg-white rounded-lg p-6 border-2 border-green-200">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                  📝 Upwork Cover Letter
                  <span className="text-sm font-normal text-gray-500">(Copy & Paste Ready)</span>
                </h4>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(success.upworkCoverLetter);
                    alert('Cover letter copied to clipboard!');
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors text-sm"
                >
                  📋 Copy to Clipboard
                </button>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans leading-relaxed">
                  {success.upworkCoverLetter}
                </pre>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                ✨ This cover letter is personalized based on the job posting and includes your case study PDF link.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-5 flex items-start gap-4">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-red-900">{error}</h3>
          </div>
          <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
            ✕
          </button>
        </div>
      )}

      {/* Generator Form */}
      <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-7 h-7 text-purple-600" />
            Generate New Case Study
          </h2>

          {/* Test Mode Toggle */}
          <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={testMode}
                onChange={(e) => setTestMode(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
              <span className="ml-3 text-sm font-medium text-gray-700">
                {testMode ? '🧪 Test Mode (Free)' : '🚀 AI Mode (Uses Credits)'}
              </span>
            </label>
          </div>
        </div>

        <form onSubmit={handleGenerate}>
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Client Requirement / Job Posting
              <span className="text-red-500 ml-1">*</span>
            </label>
            <textarea
              value={clientRequirement}
              onChange={(e) => setClientRequirement(e.target.value)}
              placeholder="Paste the client requirement or job posting here. Be as detailed as possible - include technical requirements, pain points, industry context, and desired outcomes. Minimum 50 characters."
              className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-200 focus:border-purple-500 transition-all min-h-[200px] font-mono text-sm"
              required
              disabled={generating}
            />
            <div className="flex justify-between items-center mt-2">
              <span className={`text-sm font-medium ${charCountColor}`}>
                {charCount} / 5000 characters {charCount < 50 && '(minimum 50)'}
              </span>
              {isValid && (
                <span className="text-sm text-green-600 font-medium">✓ Ready to generate</span>
              )}
            </div>
          </div>

          {/* File Upload Section */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Attach Document <span className="text-gray-400 font-normal">(Optional)</span>
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 hover:border-purple-400 transition-colors">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".pdf,.doc,.docx,.xls,.xlsx"
                className="hidden"
                disabled={generating || uploadingFile}
              />
              {!uploadedFile ? (
                <div
                  className="flex flex-col items-center justify-center py-4 cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="w-10 h-10 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600 font-medium">Click to upload a document</p>
                  <p className="text-xs text-gray-400 mt-1">PDF, Word (.doc, .docx), or Excel (.xls, .xlsx)</p>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <File className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{uploadedFile.name}</p>
                      <p className="text-xs text-gray-500">
                        {(uploadedFile.size / 1024).toFixed(1)} KB
                        {fileContent && ` • ${fileContent.length} characters extracted`}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={removeUploadedFile}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Remove file"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              )}
              {uploadingFile && (
                <div className="flex items-center justify-center py-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
                  <span className="ml-2 text-sm text-purple-600">Extracting content...</span>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              The document content will be analyzed along with the text above for better cover letter and case study generation.
            </p>
          </div>

          {/* Prompt Selection - Only show in AI mode */}
          {!testMode && (
            <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-blue-900 text-sm flex items-center gap-2">
                  🤖 Cover Letter Prompt (ChatGPT)
                </h3>
                <button
                  type="button"
                  onClick={() => setShowPromptManager(!showPromptManager)}
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  <Settings className="w-4 h-4" />
                  Manage Prompts
                </button>
              </div>
              <select
                value={selectedPromptId || ''}
                onChange={(e) => setSelectedPromptId(e.target.value)}
                className="w-full px-4 py-3 border-2 border-blue-200 rounded-lg bg-white focus:ring-2 focus:ring-blue-300 focus:border-blue-400"
                disabled={generating}
              >
                {prompts.map(prompt => (
                  <option key={prompt.id} value={prompt.id}>
                    {prompt.name} {prompt.isDefault ? '(Default)' : ''} - {prompt.model}
                  </option>
                ))}
              </select>
              {selectedPrompt && (
                <p className="text-xs text-blue-700 mt-2">
                  {selectedPrompt.description || 'No description available'}
                </p>
              )}
            </div>
          )}

          {testMode ? (
            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 mb-6">
              <h3 className="font-semibold text-yellow-900 mb-2 text-sm">🧪 Test Mode Enabled</h3>
              <ul className="text-sm text-yellow-800 space-y-1 list-disc list-inside">
                <li><strong>No API credits will be used</strong> - this generates a mock case study</li>
                <li>The mock generator will analyze your input and create relevant examples</li>
                <li>Perfect for testing the UI and PDF generation without costs</li>
                <li>Turn off Test Mode to use real AI-powered generation</li>
              </ul>
            </div>
          ) : (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-6">
              <h3 className="font-semibold text-blue-900 mb-2 text-sm">💡 AI Generation Mode:</h3>
              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                <li><strong>Case Study:</strong> Generated by Claude AI (analysis + case studies)</li>
                <li><strong>Cover Letter:</strong> Generated by ChatGPT using selected prompt</li>
                <li>Select a prompt above to customize your cover letter style</li>
                <li>The PDF will include both case studies and your professional profile</li>
              </ul>
            </div>
          )}

          <button
            type="submit"
            disabled={!isValid || generating}
            className="w-full py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-2xl hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-3"
          >
            {generating ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Generating Case Study... {testMode ? '(5-10 seconds)' : '(15-30 seconds)'}</span>
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                <span>{testMode ? 'Generate Test Case Study (Free)' : 'Generate Case Study with AI'}</span>
              </>
            )}
          </button>
        </form>
      </div>

      {/* Prompt Manager Section */}
      {showPromptManager && (
        <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Settings className="w-7 h-7 text-purple-600" />
              Manage Cover Letter Prompts
            </h2>
            <button
              onClick={() => setNewPrompt({
                name: '',
                description: '',
                content: '',
                model: 'gpt-5.1',
                maxTokens: 1500,
                temperature: 0.8,
                systemMessage: 'You are an expert cover letter writer who creates compelling, personalized Upwork proposals.',
                isDefault: false
              })}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-all"
            >
              <Plus className="w-4 h-4" />
              New Prompt
            </button>
          </div>

          {/* New Prompt Form */}
          {newPrompt && (
            <div className="mb-6 bg-green-50 border-2 border-green-200 rounded-xl p-6">
              <h3 className="font-bold text-green-900 mb-4 flex items-center gap-2">
                <Plus className="w-5 h-5" />
                Create New Prompt
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Prompt Name *</label>
                  <input
                    type="text"
                    value={newPrompt.name}
                    onChange={(e) => setNewPrompt({ ...newPrompt, name: e.target.value })}
                    placeholder="e.g., Aggressive Sales Pitch"
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-green-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
                  <input
                    type="text"
                    value={newPrompt.description}
                    onChange={(e) => setNewPrompt({ ...newPrompt, description: e.target.value })}
                    placeholder="Brief description of this prompt style"
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-green-300"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Model</label>
                    <select
                      value={newPrompt.model}
                      onChange={(e) => setNewPrompt({ ...newPrompt, model: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-green-300"
                    >
                      <option value="gpt-5.1">GPT-5.1 (Latest & Best)</option>
                      <option value="gpt-4o-mini">GPT-4o Mini (Fast & Cheap)</option>
                      <option value="gpt-4o">GPT-4o (Best Quality)</option>
                      <option value="gpt-4-turbo">GPT-4 Turbo</option>
                      <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Fastest)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Max Tokens</label>
                    <input
                      type="number"
                      value={newPrompt.maxTokens}
                      onChange={(e) => setNewPrompt({ ...newPrompt, maxTokens: parseInt(e.target.value) })}
                      min="100"
                      max="4000"
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-green-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Temperature</label>
                    <input
                      type="number"
                      value={newPrompt.temperature}
                      onChange={(e) => setNewPrompt({ ...newPrompt, temperature: parseFloat(e.target.value) })}
                      min="0"
                      max="2"
                      step="0.1"
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-green-300"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">System Message</label>
                  <textarea
                    value={newPrompt.systemMessage}
                    onChange={(e) => setNewPrompt({ ...newPrompt, systemMessage: e.target.value })}
                    placeholder="Instructions for the AI's behavior and role"
                    rows={2}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-green-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Prompt Content *
                    <span className="text-xs font-normal text-gray-500 ml-2">
                      (Use placeholders: {'{{requirement}}'}, {'{{pdfUrl}}'}, {'{{industryContext}}'}, {'{{keyPainPoints}}'}, {'{{technicalRequirements}}'}, {'{{businessObjectives}}'})
                    </span>
                  </label>
                  <textarea
                    value={newPrompt.content}
                    onChange={(e) => setNewPrompt({ ...newPrompt, content: e.target.value })}
                    placeholder="Enter your prompt template here. Use {{requirement}} for the job posting and {{pdfUrl}} for the case study link."
                    rows={8}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-green-300 font-mono text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="newPromptDefault"
                    checked={newPrompt.isDefault}
                    onChange={(e) => setNewPrompt({ ...newPrompt, isDefault: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <label htmlFor="newPromptDefault" className="text-sm text-gray-700">Set as default prompt</label>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleCreatePrompt}
                    disabled={!newPrompt.name || !newPrompt.content || newPrompt.content.length < 50}
                    className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-all disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" />
                    Save Prompt
                  </button>
                  <button
                    onClick={() => setNewPrompt(null)}
                    className="flex items-center gap-2 px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-all"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Prompts List */}
          <div className="space-y-4">
            {prompts.map((prompt) => (
              <div
                key={prompt.id}
                className={`border-2 rounded-xl p-4 ${
                  editingPrompt?.id === prompt.id
                    ? 'border-blue-300 bg-blue-50'
                    : prompt.isDefault
                    ? 'border-purple-300 bg-purple-50'
                    : 'border-gray-200'
                }`}
              >
                {editingPrompt?.id === prompt.id ? (
                  // Edit Mode
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Name</label>
                        <input
                          type="text"
                          value={editingPrompt.name}
                          onChange={(e) => setEditingPrompt({ ...editingPrompt, name: e.target.value })}
                          className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
                        <input
                          type="text"
                          value={editingPrompt.description}
                          onChange={(e) => setEditingPrompt({ ...editingPrompt, description: e.target.value })}
                          className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Model</label>
                        <select
                          value={editingPrompt.model}
                          onChange={(e) => setEditingPrompt({ ...editingPrompt, model: e.target.value })}
                          className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg"
                        >
                          <option value="gpt-5.1">GPT-5.1 (Latest & Best)</option>
                          <option value="gpt-4o-mini">GPT-4o Mini</option>
                          <option value="gpt-4o">GPT-4o</option>
                          <option value="gpt-4-turbo">GPT-4 Turbo</option>
                          <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Max Tokens</label>
                        <input
                          type="number"
                          value={editingPrompt.maxTokens}
                          onChange={(e) => setEditingPrompt({ ...editingPrompt, maxTokens: parseInt(e.target.value) })}
                          className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Temperature</label>
                        <input
                          type="number"
                          value={editingPrompt.temperature}
                          onChange={(e) => setEditingPrompt({ ...editingPrompt, temperature: parseFloat(e.target.value) })}
                          step="0.1"
                          className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">System Message</label>
                      <textarea
                        value={editingPrompt.systemMessage}
                        onChange={(e) => setEditingPrompt({ ...editingPrompt, systemMessage: e.target.value })}
                        rows={2}
                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Prompt Content</label>
                      <textarea
                        value={editingPrompt.content}
                        onChange={(e) => setEditingPrompt({ ...editingPrompt, content: e.target.value })}
                        rows={6}
                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg font-mono text-sm"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={editingPrompt.isDefault}
                        onChange={(e) => setEditingPrompt({ ...editingPrompt, isDefault: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <label className="text-sm text-gray-700">Set as default</label>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={handleUpdatePrompt}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700"
                      >
                        <Save className="w-4 h-4" />
                        Save Changes
                      </button>
                      <button
                        onClick={() => setEditingPrompt(null)}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300"
                      >
                        <X className="w-4 h-4" />
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <div>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-bold text-gray-900 flex items-center gap-2">
                          {prompt.name}
                          {prompt.isDefault && (
                            <span className="text-xs bg-purple-200 text-purple-800 px-2 py-0.5 rounded-full">Default</span>
                          )}
                        </h3>
                        <p className="text-sm text-gray-600">{prompt.description}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">{prompt.model}</span>
                        <button
                          onClick={() => setEditingPrompt({ ...prompt })}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-all"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setPromptDeleteConfirm(prompt)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 flex items-center gap-4">
                      <span>Tokens: {prompt.maxTokens}</span>
                      <span>Temp: {prompt.temperature}</span>
                      <span>Updated: {new Date(prompt.updatedAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Case Studies List */}
      <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-7 h-7 text-purple-600" />
            Generated Case Studies ({caseStudies.length})
          </h2>
          <div className="flex items-center gap-4">
            {/* Selection controls */}
            {caseStudies.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={selectAllCaseStudies}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all"
                >
                  {selectedCaseStudies.length === caseStudies.length ? (
                    <CheckSquare className="w-4 h-4 text-purple-600" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                  {selectedCaseStudies.length === caseStudies.length ? 'Deselect All' : 'Select All'}
                </button>
                {selectedCaseStudies.length > 0 && (
                  <button
                    onClick={deleteSelectedCaseStudies}
                    disabled={deletingMultiple}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-all disabled:opacity-50"
                  >
                    {deletingMultiple ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        Delete Selected ({selectedCaseStudies.length})
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search case studies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-200 focus:border-purple-500 transition-all"
              />
            </div>
          </div>
        </div>

        {filteredCaseStudies.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">
              {searchQuery ? 'No case studies match your search.' : 'No case studies generated yet. Create your first one above!'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredCaseStudies.map((cs) => (
              <div
                key={cs.id}
                className={`border-2 rounded-xl p-6 hover:shadow-md transition-all ${
                  selectedCaseStudies.includes(cs.id)
                    ? 'border-purple-400 bg-purple-50'
                    : 'border-gray-200 hover:border-purple-300'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-4 flex-1">
                    {/* Selection checkbox */}
                    <button
                      onClick={() => toggleCaseStudySelection(cs.id)}
                      className="mt-1 flex-shrink-0"
                    >
                      {selectedCaseStudies.includes(cs.id) ? (
                        <CheckSquare className="w-5 h-5 text-purple-600" />
                      ) : (
                        <Square className="w-5 h-5 text-gray-400 hover:text-purple-600" />
                      )}
                    </button>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{cs.title}</h3>
                    <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {new Date(cs.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                      <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full font-medium">
                        {cs.painPoint}
                      </span>
                      {cs.coverLetterSource && (
                        <span className={`px-3 py-1 rounded-full font-medium ${
                          cs.coverLetterSource === 'openai'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {cs.coverLetterSource === 'openai' ? '🤖 ChatGPT' : '🧠 Claude'}
                        </span>
                      )}
                    </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-700 line-clamp-3">{cs.clientRequirement}</p>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                  <a
                    href={cs.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-semibold hover:shadow-lg hover:scale-105 transition-all"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View PDF
                  </a>
                  <a
                    href={cs.pdfUrl}
                    download
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 hover:shadow-lg hover:scale-105 transition-all"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </a>
                  <button
                    onClick={() => navigator.clipboard.writeText(cs.pdfUrl)}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-all"
                  >
                    Copy Link
                  </button>
                  <button
                    onClick={() => setDeleteConfirm({ id: cs.id, title: cs.title })}
                    className="ml-auto flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg font-semibold hover:bg-red-200 transition-all"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Case Study Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Delete Case Study?</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "<strong>{deleteConfirm.title}</strong>"? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => handleDelete(deleteConfirm.id)}
                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Prompt Confirmation Modal */}
      {promptDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Delete Prompt?</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "<strong>{promptDeleteConfirm.name}</strong>"? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => handleDeletePrompt(promptDeleteConfirm.id)}
                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
              <button
                onClick={() => setPromptDeleteConfirm(null)}
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
